import React, { useEffect, useState } from 'react';
import { Prompt, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { FaPen, FaSave } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';
import { FormBuilder } from '../../../../components/FormBuilder';
import PageContainer from '../../../../components/PageContainer';
import Loading from '../../../../components/Loading';
import useAlertNotification from '../../../../context/AlertNotificationContext';
import ErrorRequestService from '../../../../services/errorRequest';
import FuncaoApiService from '../../../../services/api/FuncaoApiService';
import FuncaoValidation from '../../../../services/validation/FuncaoValidation';
import UtilService from '../../../../services/util';
import FuncaoViewInfo from './FuncaoViewInfo';
import { SyncStatusTipo, SyncStatus } from '../../../../util/enum';
import { consultaStatusSincronizacao } from '../../../../services/api/SyncConvertConvenia';

function FuncaoView(props) {
    // CONTEXT
    const match = useRouteMatch();
    const location = useLocation();
    const history = useHistory();
    const alert = useAlertNotification();

    // PROPS
    const { nome='', cbo='' } = location.state || {};
    const { cod } = match.params;

    // STATE
    const [origData, setOrginData] = useState(null);
    const [formFields, setFormFields] = useState({});
    const [formData, setFormData] = useState(null);
    const [edit, setEdit] = useState(false);
    const [isEdited, setIsEdited] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [statusSyncMessage, setStatusSyncMessage] = useState(null);

    // FUNCTIONS
    const isFormEdited = () => {
        const jsonOrigData = JSON.stringify(origData);
        const jsonFormData = JSON.stringify(formData);
        return (edit) && (jsonOrigData !== jsonFormData);
    }
        
    const askForLeave = (cbPositive, cbNegative=()=>{}) => {
        if (cbPositive instanceof Function) {
            const msg = 'Foram feitas alterações!\nDeseja realmente sair e perdê-las?';
            const leave = window.confirm(msg);
            
            if (leave) cbPositive();
            else cbNegative();
        }   
        return false;
    }

    const resetForm = () => UtilService.genericResetFormFunction();

    const saveForm = async () => {
        try {
            // validar form
            const validation = await FuncaoValidation.validate(formData);
            if (!validation.isValid) {
                alert.error("Erros no formulário!");
                return;
            }
            
            // se ok, enviar p/ API
            const resp = await FuncaoApiService.updateOne(formData.cod, formData);
            if (resp.data.result) {
                setIsEdited(false);
                setEdit(false);
                alert.success("Dados da funcão atualizados!");
                const url = `${match.url.replace(`/${cod}`, '')}`;
                history.replace(url, location.state);
            }
        }
        catch (err) {
            const errorService = new ErrorRequestService(err);
            const errorMsg = errorService.getErrors(); 
            console.log(errorMsg);
            alert.error(errorMsg);
        }
    }

    const toggleEdit = () => {
        if (isEdited) {
            askForLeave(() => {
                setFormData(origData);
                setEdit(!edit);
                resetForm();
            });
            return;
        }
        setEdit(!edit);
    }

    const handleCancel = () => {
        if (isEdited) {
            askForLeave(() => {
                setIsEdited(false);
                setFormData(origData);
                setEdit(!edit);
                resetForm();
            });
            return;
        }

        if (edit) {
            setEdit(!edit);
            return;
        }

        history.goBack();
    }
    
    const onChange = (ev) => {
        const { value, name } = ev.target;
        const newform = {...formData, [name]: value};
        setFormData(newform);
    }

    const onEdit = () => {
        if (isFormEdited() && !isEdited) setIsEdited(true);
    }

    const onStart = () => {
        const getData = async () => {
            try {
                setIsLoading(true);
                const [ formFields, formData ] = await Promise.all([
                    FuncaoViewInfo.getFormFields(),
                    FuncaoApiService.getByCod(cod).then(resp => {
                        const { data } = resp.data;
                        return data;
                    })
                ]);
                setFormFields( formFields );
                setFormData( formData );
                setOrginData( formData );
            }
            catch (err) {
                const errorService = new ErrorRequestService(err);
                const errorMsg = errorService.getErrors(); 
                console.log(errorMsg);
                alert.error(errorMsg);
            }
            finally {
                setIsLoading(false);
            }
        }

        const handleLoadingStatusSyncConvenia = async () => {
            try {
                const resp = await consultaStatusSincronizacao(SyncStatusTipo.FUNCOES);
                if (resp.data) {
                    setStatusSyncMessage(resp.data.status);
                }
            }
            catch (err) {
                const errorService = new ErrorRequestService(err);
                const errorMsg = errorService.getErrors();
                console.error(errorMsg);
                alert.error(errorMsg);
            }
        }

        handleLoadingStatusSyncConvenia();
        getData();
    }

    // USEEFFECTS
    useEffect(onStart, []);
    useEffect(onEdit, [formData]);

    const actions = [
        ...( !edit ? [
            { 
                label: 'Editar',
                icon: <FaPen />,
                color: 'primary',
                type: 'button',
                onClick: toggleEdit,
                disabled: statusSyncMessage === SyncStatus.IN_PROGRESS
            }
        ]:[
            { 
                label: 'Salvar',
                icon: <FaSave />,
                color: 'success',
                type: 'button',
                onClick: saveForm
            }
        ]),
        { 
            label: 'Cancelar',
            icon: <MdClose /> ,
            color: 'danger',
            type: 'button',
            onClick: handleCancel
        }
    ]
    
    const title = `Função ${nome} (${cbo})`;

    return (
        <PageContainer
            title={title}
            actions={actions}
        >
            { isLoading 
                ? <Loading message="Carregando..." />
                : ( origData && 
                    <FormBuilder 
                        formFields={formFields}
                        formData={formData}
                        onChange={onChange}
                        disabledForm={!edit}
                        initialValues={origData}
                        schema={FuncaoValidation.schema}
                    />
                )
            }
            
            <Prompt
                when={isEdited}
                message={location => askForLeave(() => {
                    setIsEdited(false);
                    setTimeout(() => {
                        resetForm();
                        history.push(location.pathname);
                    }, 100);
                })}
            />
        </PageContainer>
    )
}

export default FuncaoView;
